import { useEffect, useMemo, useState } from "react";
import { Key, Loader2, Plus, Search, Trash2, UserCog, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const ROLES = [
  ["superadmin", "Superadmin"],
  ["org_owner", "Org eigenaar"],
  ["org_admin", "Org beheerder"],
  ["event_user", "Event user"],
  ["traffic_coordinator", "Verkeersbeheerder"],
  ["scanner", "Scanner"],
];

const ROLE_STYLES = {
  superadmin: "bg-gradient-to-r from-svj-orange/20 via-svj-pink/20 to-svj-purple/20 text-white border-svj-pink/50",
  org_owner: "bg-svj-cyan/15 border-svj-cyan/40 text-svj-cyan",
  org_admin: "bg-svj-cyan/10 border-svj-cyan/30 text-svj-cyan",
  event_user: "bg-svj-purple/15 border-svj-purple/40 text-svj-purple",
  traffic_coordinator: "bg-svj-orange/15 border-svj-orange/40 text-svj-orange",
  scanner: "bg-svj-border border-svj-border text-slate-400",
};

// 'event_user' (full event management) and 'traffic_coordinator' (StapKompas
// Verkeer only — Verkeersregelaars/Live Flow/Incidenten/Chat) are both
// confined to exactly one event, the same way, for the same reason: see
// require_traffic_access()/_check_event_scope() in backend/server.py.
function roleRequiresOneEvent(role) {
  return role === "event_user" || role === "traffic_coordinator";
}

function emptyForm(defaultRole = "org_admin") {
  return { email: "", password: "", first_name: "", last_name: "", role: defaultRole, organization_id: "", event_id: "", event_day_ids: [] };
}

function generatePassword(len = 14) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function AdminUsers() {
  const { authAxios, me } = useAuth();
  // An event_user manages only the scanner accounts scoped to their own event —
  // the backend (admin_list_users/admin_create_user/etc.) already enforces this;
  // this view is a matching simplified rendering mode, not a second permission check.
  const isEventUsersView = me?.role === "event_user";
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [scannerEventDays, setScannerEventDays] = useState([]);
  const [editScannerEventDays, setEditScannerEventDays] = useState([]);

  // Multi-day registration ("Lichtjestour"-style events) day-scoped scanner accounts:
  // the event a new scanner account would be scoped to -- either explicitly chosen
  // (org/superadmin view) or forced to the event_user's own single event.
  const scannerScopeEventId = isEventUsersView ? events[0]?.id : (form.role === "scanner" ? form.event_id : "");
  const scannerScopeEvent = events.find((ev) => ev.id === scannerScopeEventId);

  useEffect(() => {
    setForm((f) => (f.event_day_ids.length ? { ...f, event_day_ids: [] } : f));
    if (!scannerScopeEventId || !scannerScopeEvent?.multi_day_registration_enabled) {
      setScannerEventDays([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const { data } = await authAxios.get(`/admin/events/${scannerScopeEventId}/days`);
        if (!ignore) setScannerEventDays(data.items || []);
      } catch {
        if (!ignore) setScannerEventDays([]);
      }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerScopeEventId, scannerScopeEvent?.multi_day_registration_enabled]);

  // Same day-scoping, but for the EDIT row of an already-existing scanner account —
  // previously this only existed on the create form, so once a scan account's day
  // restriction was set at creation, it could never be corrected afterward (a real
  // reported gap). editForm.event_id is only meaningful while editingId is set.
  const editScopeEventId = isEventUsersView ? events[0]?.id : (editForm.role === "scanner" ? editForm.event_id : "");
  const editScopeEvent = events.find((ev) => ev.id === editScopeEventId);

  useEffect(() => {
    if (!editingId || !editScopeEventId || !editScopeEvent?.multi_day_registration_enabled) {
      setEditScannerEventDays([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const { data } = await authAxios.get(`/admin/events/${editScopeEventId}/days`);
        if (!ignore) setEditScannerEventDays(data.items || []);
      } catch {
        if (!ignore) setEditScannerEventDays([]);
      }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, editScopeEventId, editScopeEvent?.multi_day_registration_enabled]);

  const load = async () => {
    setLoading(true);
    try {
      const [u, o, ev] = await Promise.all([
        authAxios.get("/admin/users"),
        authAxios.get("/admin/organizations"),
        authAxios.get("/admin/events"),
      ]);
      setUsers(u.data.items || []);
      setOrgs(o.data.items || []);
      setEvents(ev.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.email, u.first_name, u.last_name, u.role, u.organizations?.name]
        .filter(Boolean).join(" ").toLowerCase().includes(q),
    );
  }, [users, query]);

  const create = async () => {
    if (!form.email || !form.password || !form.first_name || !form.last_name) {
      return toast.error("Vul alle verplichte velden in");
    }
    if (!isEventUsersView) {
      if (form.role !== "superadmin" && !form.organization_id) {
        return toast.error("Kies een organisatie");
      }
      if (roleRequiresOneEvent(form.role) && !form.event_id) {
        return toast.error("Kies een event");
      }
    }
    setCreating(true);
    try {
      // An event_user's own account is always scoped to creating 'scanner'
      // accounts for their own event — the backend forces organization_id/
      // event_id server-side regardless of what's sent, so those fields are
      // simply omitted here rather than duplicating that logic in the UI.
      await authAxios.post("/admin/users", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: isEventUsersView ? "scanner" : form.role,
        ...(isEventUsersView
          ? {}
          : {
              organization_id: form.role === "superadmin" ? null : form.organization_id,
              event_id: (roleRequiresOneEvent(form.role) || form.role === "scanner") ? (form.event_id || null) : null,
            }),
        // Multi-day registration ("Lichtjestour"-style events) day-scoped scanner
        // accounts -- only meaningful for a scanner scoped to a flagged event.
        ...(scannerScopeEvent?.multi_day_registration_enabled ? { event_day_ids: form.event_day_ids } : {}),
      });
      toast.success(`Account aangemaakt · tijdelijk wachtwoord: ${form.password}`);
      setForm(emptyForm(isEventUsersView ? "scanner" : "org_admin"));
      setShowCreate(false);
      load();
    } catch (e) {
      const raw = e?.response?.data?.detail || e.message;
      // If server returns a long/technical message, trim to first sentence
      const msg = String(raw).split("\n")[0].slice(0, 200);
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditForm({
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      role: u.role,
      organization_id: u.organization_id || "",
      event_id: u.event_id || "",
      active: u.active !== false,
      event_day_ids: u.event_day_ids || [],
    });
  };

  const saveEdit = async (userId) => {
    if (!isEventUsersView && roleRequiresOneEvent(editForm.role) && !editForm.event_id) {
      return toast.error("Kies een event");
    }
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (payload.role === "superadmin") payload.organization_id = null;
      if (!roleRequiresOneEvent(payload.role) && payload.role !== "scanner") payload.event_id = null;
      // Only send event_day_ids when it's actually meaningful (a scanner scoped to
      // a multi-day-registration event) -- omitting it on every other save leaves
      // an existing day restriction untouched rather than accidentally clearing it.
      if (payload.role !== "scanner" || !editScopeEvent?.multi_day_registration_enabled) {
        delete payload.event_day_ids;
      }
      await authAxios.patch(`/admin/users/${userId}`, payload);
      toast.success("Bijgewerkt");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Account ${u.email} verwijderen? Dit kan niet ongedaan gemaakt worden.`)) return;
    try {
      await authAxios.delete(`/admin/users/${u.id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const resetPassword = async (u) => {
    const pwd = window.prompt(`Nieuw tijdelijk wachtwoord voor ${u.email}:`, generatePassword());
    if (!pwd) return;
    if (pwd.length < 8) return toast.error("Minimaal 8 tekens");
    setResetting(u.id);
    try {
      await authAxios.post(`/admin/users/${u.id}/password`, { password: pwd });
      toast.success(`Wachtwoord gezet · deel dit met de gebruiker: ${pwd}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setResetting(null);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Beheer</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">Gebruikers</h1>
          <p className="mt-1 text-sm text-slate-400">
            {isEventUsersView
              ? `Scan-accounts voor ${me?.event?.title || "je event"}.`
              : "Alleen superadmins kunnen accounts aanmaken, wijzigen en verwijderen."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setForm({ ...emptyForm(isEventUsersView ? "scanner" : "org_admin"), password: generatePassword() }); setShowCreate(true); }}
          data-testid="admin-users-new"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2.5 px-5 text-sm hover:shadow-[0_0_24px_rgba(214,61,122,0.4)] transition-all"
        >
          <Plus className="h-4 w-4" /> {isEventUsersView ? "Nieuw scan-account" : "Nieuw account"}
        </button>
      </div>

      <div className="mt-6 relative max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek op naam, e-mail, organisatie of rol…"
          data-testid="admin-users-search"
          className="w-full h-11 pl-11 pr-4 rounded-full bg-svj-card border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
        />
      </div>

      {showCreate && (
        <div className="mt-6 rounded-2xl border border-svj-border bg-svj-card p-5" data-testid="admin-users-create-panel">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-white uppercase tracking-wider">
              {isEventUsersView ? "Nieuw scan-account" : "Nieuw account"}
            </div>
            <button type="button" onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">E-mail *</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Voornaam *</span>
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="new-user-firstname" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Achternaam *</span>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="new-user-lastname" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            {!isEventUsersView && (
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Rol *</span>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
                  {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
            )}
            {!isEventUsersView && form.role !== "superadmin" && (
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Organisatie *</span>
                <select value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value, event_id: "" })} data-testid="new-user-org" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
                  <option value="">Kies organisatie…</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </label>
            )}
            {!isEventUsersView && (roleRequiresOneEvent(form.role) || form.role === "scanner") && (
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  {roleRequiresOneEvent(form.role) ? "Event *" : "Event (optioneel — leeg = alle events)"}
                </span>
                <select value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} data-testid="new-user-event" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
                  <option value="">{roleRequiresOneEvent(form.role) ? "Kies event…" : "— geen (org-breed) —"}</option>
                  {events
                    .filter((ev) => !form.organization_id || ev.organization_id === form.organization_id)
                    .map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                </select>
              </label>
            )}
            {(isEventUsersView || form.role === "scanner") && scannerScopeEvent?.multi_day_registration_enabled && (
              <label className="block sm:col-span-2 lg:col-span-3">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Dagen (leeg = alle dagen)
                </span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {scannerEventDays.map((d) => {
                    const checked = form.event_day_ids.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          event_day_ids: checked ? form.event_day_ids.filter((x) => x !== d.id) : [...form.event_day_ids, d.id],
                        })}
                        data-testid={`new-user-day-${d.id}`}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          checked
                            ? "bg-svj-purple/20 border-svj-purple/60 text-svj-purple"
                            : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                        }`}
                      >
                        {d.label || `Dag ${d.day_number}`}
                      </button>
                    );
                  })}
                  {scannerEventDays.length === 0 && (
                    <span className="text-xs text-slate-500">Geen dagen ingesteld voor dit event.</span>
                  )}
                </div>
              </label>
            )}
            <label className="block sm:col-span-2 lg:col-span-3">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Tijdelijk wachtwoord *</span>
              <div className="mt-1 flex gap-2">
                <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" className="flex-1 h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
                <button type="button" onClick={() => setForm({ ...form, password: generatePassword() })} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3">Genereer</button>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">Kopieer dit wachtwoord en deel het met de gebruiker.</div>
            </label>
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowCreate(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-sm font-semibold px-4 py-2">Annuleren</button>
            <button type="button" onClick={create} disabled={creating} data-testid="new-user-submit" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Aanmaken
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-svj-border overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-svj-card/60 text-[11px] uppercase tracking-widest text-slate-400 font-semibold border-b border-svj-border">
          <div className="col-span-4">Naam / e-mail</div>
          <div className="col-span-3">Organisatie</div>
          <div className="col-span-2">Rol</div>
          <div className="col-span-1">Actief</div>
          <div className="col-span-2 text-right">Acties</div>
        </div>
        {loading && <div className="p-8 text-sm text-slate-400">Laden…</div>}
        {!loading && filtered.map((u) => {
          const isEditing = editingId === u.id;
          const isSelf = u.id === me?.user_id;
          const isPlatformSuper = u.email === "info@studiovoorjou.nl";
          return (
            <div key={u.id} data-testid={`admin-user-row-${u.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 items-center bg-svj-card border-b border-svj-border last:border-b-0">
              <div className="md:col-span-4 min-w-0">
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} className="h-9 px-2 rounded bg-svj-bg border border-svj-border text-white text-sm" />
                    <input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} className="h-9 px-2 rounded bg-svj-bg border border-svj-border text-white text-sm" />
                  </div>
                ) : (
                  <>
                    <div className="text-white font-semibold truncate">
                      {u.first_name} {u.last_name}
                      {isSelf && <span className="ml-2 text-[10px] uppercase tracking-widest bg-svj-cyan/20 text-svj-cyan rounded-full px-2 py-0.5">Jij</span>}
                    </div>
                    <div className="text-xs text-slate-400 truncate">{u.email}</div>
                  </>
                )}
              </div>
              <div className="md:col-span-3">
                {isEditing && !isEventUsersView && editForm.role !== "superadmin" ? (
                  <select value={editForm.organization_id || ""} onChange={(e) => setEditForm({ ...editForm, organization_id: e.target.value })} className="w-full h-9 px-2 rounded bg-svj-bg border border-svj-border text-white text-sm">
                    <option value="">— geen —</option>
                    {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                ) : (
                  <div className="text-sm text-slate-300 truncate">
                    {isEventUsersView ? (me?.event?.title || "—") : (u.organizations?.name || "—")}
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                {isEditing && !isEventUsersView ? (
                  <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} disabled={isPlatformSuper} className="w-full h-9 px-2 rounded bg-svj-bg border border-svj-border text-white text-sm disabled:opacity-50">
                    {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                ) : (
                  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${ROLE_STYLES[u.role] || ROLE_STYLES.scanner}`}>
                    {u.role}
                  </span>
                )}
              </div>
              <div className="md:col-span-1">
                {isEditing ? (
                  <input type="checkbox" checked={!!editForm.active} disabled={isPlatformSuper} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} className="h-4 w-4" />
                ) : (
                  <span className={`inline-flex h-2.5 w-2.5 rounded-full ${u.active ? "bg-emerald-400" : "bg-slate-500"}`} title={u.active ? "Actief" : "Inactief"} />
                )}
              </div>
              {isEditing && !isEventUsersView && (roleRequiresOneEvent(editForm.role) || editForm.role === "scanner") && (
                <div className="md:col-span-12">
                  <label className="block max-w-md">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                      Event{roleRequiresOneEvent(editForm.role) ? " *" : " (optioneel — leeg = alle events)"}
                    </span>
                    <select
                      value={editForm.event_id || ""}
                      onChange={(e) => setEditForm({ ...editForm, event_id: e.target.value })}
                      data-testid={`user-edit-event-${u.id}`}
                      className="mt-1 w-full h-9 px-2 rounded bg-svj-bg border border-svj-border text-white text-sm"
                    >
                      <option value="">{roleRequiresOneEvent(editForm.role) ? "Kies event…" : "— geen (org-breed) —"}</option>
                      {events
                        .filter((ev) => !editForm.organization_id || ev.organization_id === editForm.organization_id)
                        .map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                    </select>
                  </label>
                </div>
              )}
              {isEditing && (isEventUsersView || editForm.role === "scanner") && editScopeEvent?.multi_day_registration_enabled && (
                <div className="md:col-span-12">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Dagen (leeg = alle dagen)
                  </span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {editScannerEventDays.map((d) => {
                      const checked = (editForm.event_day_ids || []).includes(d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setEditForm({
                            ...editForm,
                            event_day_ids: checked
                              ? (editForm.event_day_ids || []).filter((x) => x !== d.id)
                              : [...(editForm.event_day_ids || []), d.id],
                          })}
                          data-testid={`user-edit-day-${u.id}-${d.id}`}
                          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                            checked
                              ? "bg-svj-purple/20 border-svj-purple/60 text-svj-purple"
                              : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                          }`}
                        >
                          {d.label || `Dag ${d.day_number}`}
                        </button>
                      );
                    })}
                    {editScannerEventDays.length === 0 && (
                      <span className="text-xs text-slate-500">Geen dagen ingesteld voor dit event.</span>
                    )}
                  </div>
                </div>
              )}
              {!isEditing && !isEventUsersView && u.event_id && (roleRequiresOneEvent(u.role) || u.role === "scanner") && (
                <div className="md:col-span-12 -mt-1 text-[11px] text-slate-500">
                  Gekoppeld event: {u.ticket_events?.title || u.event_id}
                </div>
              )}
              <div className="md:col-span-2 flex md:justify-end items-center gap-1.5">
                {isEditing ? (
                  <>
                    <button type="button" onClick={() => setEditingId(null)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleer</button>
                    <button type="button" onClick={() => saveEdit(u.id)} disabled={saving} data-testid={`user-save-${u.id}`} className="rounded-lg bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white text-xs font-bold px-3 py-1.5 inline-flex items-center gap-1">
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Opslaan"}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => resetPassword(u)} disabled={resetting === u.id} data-testid={`user-reset-${u.id}`} title="Wachtwoord resetten" className="h-8 w-8 rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white flex items-center justify-center">
                      {resetting === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                    </button>
                    <button type="button" onClick={() => startEdit(u)} data-testid={`user-edit-${u.id}`} className="h-8 w-8 rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white flex items-center justify-center">
                      <UserCog className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => del(u)} disabled={isSelf || isPlatformSuper} data-testid={`user-delete-${u.id}`} title={isSelf ? "Je kunt jezelf niet verwijderen" : isPlatformSuper ? "Beschermd account" : "Verwijder"} className="h-8 w-8 rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 flex items-center justify-center disabled:opacity-40 disabled:hover:text-slate-300 disabled:hover:border-svj-border">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="admin-users-empty">Geen gebruikers gevonden.</div>
        )}
      </div>
    </div>
  );
}
